import { useAuthentication } from '@ecosystem/newron-client-lib';
import { PreferenceApiService } from '@services/userPreferences/PreferencesApi.service';
import { useQuery } from '@tanstack/react-query';
import { Preference } from '@utils/userPreferencesUtils/Types';
import { useMemo } from 'react';

export interface GetPreferencesQueryResponse<T> {
  data: Preference<T>[];
  isSuccess: boolean;
  isError: boolean;
  isFetching: boolean;
  isLoading: boolean;
  refetch: () => {};
}

/**
 * @return GetPreferencesQueryResponse Object
 *
 * @remarks
 * Hooks for get preferences api call using useQuery to manage server state
 *
 * @param type       - to difference the different pages (example: "category-leaderboard-group" for Category Leaderboard Page)
 * @param namespace  - combination of retailer client and country
 * **/
export const useGetPreferences = <T>(type: string, namespace: string): GetPreferencesQueryResponse<T> => {
  const { getAccessToken } = useAuthentication();

  const { data, isSuccess, isError, isFetching, isLoading, refetch } = useQuery({
    queryKey: ['preferences', type, namespace],
    queryFn: async () => {
      const userToken = await getAccessToken();
      return PreferenceApiService.getPreferences<T>(userToken ?? '', type, namespace);
    },
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  return useMemo(() => {
    return {
      data: data as Preference<T>[],
      isFetching,
      isLoading,
      isError,
      isSuccess,
      refetch,
    };
  }, [data, isFetching, isLoading, isError, isSuccess]);
};
